import React from 'react';
import { useTranslation } from 'react-i18next';
import Loading from '../../../../components/loading';
import { ToastContext, Button, UserAccess } from '@weezevent/nacre';
import UploadFileZone from '../../../../components/upload/uploadFileZone';
import Chart from '../../../../models/chart';
import DeletionModal from '../../../../components/deletionModal';
import ZoneEditorModal from './zoneEditor/zoneEditorModal';
import { usePermissions } from '../../../../user/permissions';

export function ButtonZoneEditorAccess({ image, setEditChart }) {
    const permissions = usePermissions();
    const { t } = useTranslation();

    return (
        <UserAccess
            allow={['map_2d', 'admin', 'owner']}
            capacities={permissions.roles}
            render={is_allowed => {
                if (!is_allowed) {
                    return null;
                }
                return <Button primary inverted label={t('ticket.charts.custom.chart.cta')} disabled={!image} onClick={() => setEditChart(true)} />;
            }}
        />
    );
}

export default function ChartContainer({ organization, zone, zones, chartId, syncZones }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);

    const [image, setImage] = React.useState(null);
    const [editChart, setEditChart] = React.useState(false);
    const [deletingImage, setDeletingImage] = React.useState(false);
    const [chart, loadingChart, syncChart] = Chart.useApiModel(
        {
            id: chartId,
            organizationId: organization.id,
            onSuccess: chart => {
                if (chart.preview_picture?.url) {
                    setImage(chart.preview_picture?.url);
                }
            }
        },
        [chartId, organization]
    );
    const updateChartImage = React.useCallback(
        image => {
            chart.preview_picture_id = image ? image.uuid : image;
            setImage(image ? image.url : null);
            chart
                .save({ organizationId: organization.id })
                .then(() => {
                    syncChart();
                    toast.success(t('ticket.charts.chart.imageUpdatedSuccessfully'));
                })
                .catch(e => {
                    console.error(e);
                    toast.error(t('ticket.charts.chart.imageUpdatedError'));
                });
        },
        [chart, toast, organization, syncChart]
    );

    const onCloseEditChart = React.useCallback(() => {
        setEditChart(false);
        syncZones();
    }, [syncZones]);

    if (loadingChart) {
        return <Loading />;
    }

    return (
        <>
            <ZoneEditorModal open={editChart} chart={chart} organization={organization} zone={zone} zones={zones} onClose={onCloseEditChart} />
            <DeletionModal
                onClose={() => setDeletingImage(false)}
                onAccept={() => {
                    setDeletingImage(false);
                    updateChartImage(null);
                }}
                objectToDelete={deletingImage}
            />
            <UploadFileZone
                organization={organization}
                onUpload={image => updateChartImage(image)}
                onError={() => toast.error(t('ticket.charts.chart.uploadImageError'))}
                labelDropzone={t('ticket.event.dropzone_label')}
                subLabelDropzone={t('ticket.event.dropzone_label_min_size')}
                file={image}
                onDelete={() => setDeletingImage(true)}
                maxPixels={2400 * 2400}
            />
            <ButtonZoneEditorAccess image={image} setEditChart={setEditChart} />
        </>
    );
}
